import type { MotionProps, TargetAndTransition, Transition } from 'framer-motion';
import type { CcontainerProps } from './Ccontainer.types';

const HOVER_Y_OFFSET = -2;
const PRESS_Y_OFFSET = 2;
const PRESS_SCALE = 0.98;
const MILLISECONDS_PER_SECOND = 1000;

interface BuildContainerMotionConfigInput {
  clickAnimation: boolean;
  onClick?: CcontainerProps['onClick'];
  animationDuration: number;
  preserveStyle?: CcontainerProps['preserveStyle'];
  rootMotion?: CcontainerProps['rootMotion'];
  isPressed: boolean;
}

export interface ContainerMotionConfig {
  useClickMotion: boolean;
  clickMotionProps: MotionProps;
  effectiveAnimate?: MotionProps['animate'];
  effectiveTransition?: MotionProps['transition'];
}

export function buildContainerMotionConfig(input: BuildContainerMotionConfigInput): ContainerMotionConfig {
  const useClickMotion = isClickMotionEnabled(input);
  const clickTransition = buildClickTransition(input.animationDuration);
  const pressAnimateOverride = buildPressAnimateOverride({
    useClickMotion,
    isPressed: input.isPressed,
    preserveStyle: input.preserveStyle,
  });

  return {
    useClickMotion,
    clickMotionProps: buildClickMotionProps({
      useClickMotion,
      preserveStyle: input.preserveStyle,
      clickTransition,
    }),
    effectiveAnimate: mergeAnimateOverride({
      baseRootAnimate: input.rootMotion?.animate,
      pressAnimateOverride,
    }),
    effectiveTransition: resolveEffectiveTransition({
      useClickMotion,
      rootTransition: input.rootMotion?.transition,
      clickTransition,
    }),
  };
}

function isClickMotionEnabled(input: BuildContainerMotionConfigInput): boolean {
  return Boolean(input.clickAnimation && input.onClick);
}

function buildClickTransition(animationDuration: number): Transition {
  return {
    type: 'tween',
    duration: animationDuration / MILLISECONDS_PER_SECOND,
    ease: 'easeInOut',
  };
}

function buildClickMotionProps(input: {
  useClickMotion: boolean;
  preserveStyle?: CcontainerProps['preserveStyle'];
  clickTransition: Transition;
}): MotionProps {
  if (!input.useClickMotion) {
    return {};
  }

  return {
    whileHover: {
      ...input.preserveStyle,
      y: HOVER_Y_OFFSET,
      transition: input.clickTransition,
    },
  };
}

function buildPressAnimateOverride(input: {
  useClickMotion: boolean;
  isPressed: boolean;
  preserveStyle?: CcontainerProps['preserveStyle'];
}): TargetAndTransition | undefined {
  if (!input.useClickMotion || !input.isPressed) {
    return undefined;
  }

  return {
    ...input.preserveStyle,
    y: PRESS_Y_OFFSET,
    scale: PRESS_SCALE,
  } as TargetAndTransition;
}

function mergeAnimateOverride(input: {
  baseRootAnimate?: MotionProps['animate'];
  pressAnimateOverride?: TargetAndTransition;
}): MotionProps['animate'] {
  if (!input.pressAnimateOverride) {
    return input.baseRootAnimate;
  }

  if (isMergeableAnimate(input.baseRootAnimate)) {
    return {
      ...(input.baseRootAnimate as TargetAndTransition),
      ...input.pressAnimateOverride,
    } as TargetAndTransition;
  }

  return input.pressAnimateOverride;
}

function isMergeableAnimate(baseRootAnimate?: MotionProps['animate']): boolean {
  return typeof baseRootAnimate === 'object' && baseRootAnimate != null && !Array.isArray(baseRootAnimate);
}

function resolveEffectiveTransition(input: {
  useClickMotion: boolean;
  rootTransition?: MotionProps['transition'];
  clickTransition: Transition;
}): MotionProps['transition'] {
  if (!input.useClickMotion) {
    return input.rootTransition;
  }

  return input.rootTransition ?? input.clickTransition;
}
